/**
 * The desktop rail: conversation list, theme switch, and the new-thread button.
 *
 * Below `md` this is not rendered at all — the same controls arrive as a drawer
 * (`SidebarSheet`) and a top bar (`MobileBar`) instead. Keeping the two shells
 * separate rather than restyling one into the other is what lets the desktop
 * version keep its resize grip and its collapsed icon rail, neither of which
 * means anything on a phone.
 */

import { useEffect, useState } from "react";
import { LogOut, Menu, Plus } from "lucide-react";
import type { Conversation } from "../../shared/types.ts";
import { Button } from "./ui/button.tsx";
import { IconAction } from "./IconAction.tsx";
import { ThemeToggle } from "./ThemeToggle.tsx";
import { ReadingControls } from "./ReadingControls.tsx";
import { ThreadNav } from "./ThreadNav.tsx";
import { cn } from "../lib/utils.ts";
import { api } from "../api.ts";

interface SidebarProps {
  readonly conversations: readonly Conversation[];
  readonly activeId: string | null;
  readonly onSelect: (id: string) => void;
  readonly onCreate: () => void;
  readonly onDelete: (id: string) => void;
  readonly open: boolean;
  readonly onToggle: () => void;
}

export function Sidebar({
  conversations,
  activeId,
  onSelect,
  onCreate,
  onDelete,
  open,
  onToggle,
}: SidebarProps) {
  return (
    <aside
      data-state={open ? "expanded" : "collapsed"}
      className="hidden h-dvh min-h-0 flex-col gap-3 overflow-hidden border-r border-sidebar-border bg-sidebar p-3 font-sans text-sidebar-foreground md:flex"
    >
      {/* Collapsed, the rail leaves ~32px of usable width — two 32px buttons do
          not sit side by side in that, so the head stacks instead. */}
      <div className={cn("flex items-center gap-2", !open && "flex-col")}>
        <IconAction
          label={open ? "Hide conversations" : "Show conversations"}
          onClick={onToggle}
        >
          <Menu />
        </IconAction>

        {open && (
          <span className="flex-1 overflow-hidden font-serif text-xl whitespace-nowrap">
            sZero
          </span>
        )}

        <ReadingControls />
        <ThemeToggle />
      </div>

      <Button
        variant="outline"
        size={open ? "sm" : "icon-sm"}
        onClick={onCreate}
        aria-label="New question"
        className={cn(
          "shrink-0 border-border-control bg-sidebar-accent",
          !open && "self-center",
        )}
      >
        <Plus />
        {open && "New question"}
      </Button>

      {open && (
        <ThreadNav
          conversations={conversations}
          activeId={activeId}
          onSelect={onSelect}
          onDelete={onDelete}
        />
      )}

      <SignOut collapsed={!open} />
    </aside>
  );
}

/**
 * Sign out. Rendered only when a login was actually required — on an
 * unprotected instance the control would do nothing and only raise the
 * question of what it was for.
 */
function SignOut({ collapsed }: { collapsed: boolean }) {
  const [required, setRequired] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .session()
      .then((session) => {
        if (!cancelled) setRequired(session.required);
      })
      .catch(() => {
        // Unreachable server; hiding the control is the safe default.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!required) return null;

  const signOut = async () => {
    try {
      await api.logout();
    } finally {
      // Reload rather than unwinding state by hand: it clears the transcript,
      // the conversation list, and every cached fetch in one step.
      window.location.reload();
    }
  };

  return (
    <Button
      variant="ghost"
      size={collapsed ? "icon-sm" : "sm"}
      onClick={() => void signOut()}
      aria-label="Sign out"
      className={cn(
        "mt-auto shrink-0 justify-start text-muted-foreground hover:text-foreground",
        collapsed && "self-center justify-center",
      )}
    >
      <LogOut />
      {!collapsed && "Sign out"}
    </Button>
  );
}
